import { useState, useEffect, useCallback } from "react"
import { X, ChevronRight, ChevronLeft, Sparkles, MessageCircle, BarChart3, Shield, Activity, Cpu, Bot, Zap } from "lucide-react"

const TOUR_STORAGE_KEY = "pulse_tour_completed"

interface TourStep {
  title: string
  description: string
  icon: typeof Shield
  iconColor: string
  position: "center" | "top" | "bottom"
  highlight?: string // optional data-tour attribute to highlight
}

const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to Pulse",
    description: "Pulse is your AI-powered SRE team — 9 specialized agents that monitor CloakAI's infrastructure 24/7. They detect issues, diagnose root causes, and can even fix problems autonomously.",
    icon: Sparkles,
    iconColor: "text-indigo-400",
    position: "center",
  },
  {
    title: "Status — Your Command Center",
    description: "This is the Status tab. The hero card shows what needs your attention right now: critical incidents, warnings, or an all-clear. Below it, expandable sections show GPU workers, autopilot actions, health targets, and history.",
    icon: Shield,
    iconColor: "text-green-400",
    position: "top",
    highlight: "status-tab",
  },
  {
    title: "System Health at a Glance",
    description: "The status bar at the top shows overall system health — green means all clear, amber means degraded, red means critical. The issue count badge takes you straight to active incidents.",
    icon: Activity,
    iconColor: "text-amber-400",
    position: "top",
    highlight: "status-bar",
  },
  {
    title: "Chat — Talk to Your AI Team",
    description: "Ask questions in natural language and the right agent will answer. Try \"how are the workers?\" or \"what happened in the last hour?\". Use @mentions to talk to specific agents like @forge (GPU) or @cache (Redis).",
    icon: MessageCircle,
    iconColor: "text-accent",
    position: "center",
    highlight: "chat-tab",
  },
  {
    title: "Ops — Deep Analytics",
    description: "The Ops tab has four views: Agents (per-agent health & SLOs), Dashboard (costs, quality, usage analytics), Intelligence (circuit breakers, pattern learning), and Creators (content protection reports).",
    icon: BarChart3,
    iconColor: "text-purple-400",
    position: "center",
    highlight: "ops-tab",
  },
  {
    title: "GPU Workers",
    description: "Pulse monitors your GPU workers in real-time — heartbeat, VRAM usage, job throughput, and stuck job detection. When no workers are registered, this section will show \"none\". Connect a worker to see live GPU metrics here.",
    icon: Cpu,
    iconColor: "text-orange-400",
    position: "bottom",
  },
  {
    title: "Autopilot Mode",
    description: "Enable Autopilot in the Chat tab and Pulse will automatically fix common issues. Tier 1 actions (safe) run instantly. Tier 2 actions give you a 5-minute veto window. Tier 3 actions always need your approval.",
    icon: Bot,
    iconColor: "text-cyan-400",
    position: "center",
  },
  {
    title: "You're All Set!",
    description: "Pulse runs continuously in the background — it'll alert you when something needs attention. Right now there's no data because no GPU workers are connected yet. Once workers are registered and processing jobs, you'll see live metrics, incidents, and shift reports.",
    icon: Zap,
    iconColor: "text-yellow-400",
    position: "center",
  },
]

export function shouldShowTour(): boolean {
  return localStorage.getItem(TOUR_STORAGE_KEY) !== "true"
}

export function resetTour() {
  localStorage.removeItem(TOUR_STORAGE_KEY)
}

export default function GuidedTour({ onComplete, onSwitchTab }: {
  onComplete: () => void
  onSwitchTab?: (tab: string) => void
}) {
  const [step, setStep] = useState(0)
  const [exiting, setExiting] = useState(false)
  const total = TOUR_STEPS.length
  const current = TOUR_STEPS[step]

  // Navigate to relevant tab when step changes
  useEffect(() => {
    if (!onSwitchTab) return
    const s = TOUR_STEPS[step]
    if (s.highlight === "chat-tab") onSwitchTab("chat")
    else if (s.highlight === "ops-tab") onSwitchTab("ops")
    else if (s.highlight === "status-tab" || s.highlight === "status-bar") onSwitchTab("status")
  }, [step, onSwitchTab])

  const finish = useCallback(() => {
    setExiting(true)
    localStorage.setItem(TOUR_STORAGE_KEY, "true")
    onSwitchTab?.("status")
    setTimeout(() => onComplete(), 300)
  }, [onComplete, onSwitchTab])

  const next = useCallback(() => {
    if (step < total - 1) setStep(s => s + 1)
    else finish()
  }, [step, total, finish])

  const prev = useCallback(() => {
    if (step > 0) setStep(s => s - 1)
  }, [step])

  const skip = useCallback(() => {
    finish()
  }, [finish])

  // Keyboard nav
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "Enter") next()
      else if (e.key === "ArrowLeft") prev()
      else if (e.key === "Escape") skip()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [next, prev, skip])

  const Icon = current.icon

  const positionClass = current.position === "top"
    ? "items-start pt-24"
    : current.position === "bottom"
    ? "items-end pb-28"
    : "items-center"

  return (
    <div
      className={`fixed inset-0 z-[100] flex justify-center ${positionClass} transition-opacity duration-300 ${
        exiting ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={skip} />

      {/* Card */}
      <div
        className="relative mx-4 w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300"
        key={step}
      >
        <div className="rounded-2xl border border-border/50 bg-[#12122a] shadow-2xl shadow-indigo-500/10 overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-white/5">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>

          {/* Close button */}
          <button
            onClick={skip}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
            aria-label="Skip tour"
          >
            <X size={16} />
          </button>

          {/* Content */}
          <div className="px-6 pt-6 pb-5 space-y-4">
            {/* Icon + Step counter */}
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${current.iconColor}`}>
                <Icon size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-white">{current.title}</h3>
                <span className="text-[10px] text-gray-500 font-mono">{step + 1} of {total}</span>
              </div>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-400 leading-relaxed">
              {current.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 px-6 pb-5">
            {step > 0 ? (
              <button
                onClick={prev}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs text-gray-400 hover:text-gray-200 hover:bg-white/5 transition-colors"
              >
                <ChevronLeft size={14} /> Back
              </button>
            ) : (
              <button
                onClick={skip}
                className="px-3 py-2 rounded-lg text-xs text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
              >
                Skip tour
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={next}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors"
            >
              {step === total - 1 ? "Get Started" : "Next"}
              {step < total - 1 && <ChevronRight size={14} />}
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5 pb-4">
            {TOUR_STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "bg-indigo-400 w-4" : i < step ? "bg-indigo-400/40" : "bg-white/10"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
